/**
 * Theme Name: Biznetic
 * Theme URI: https://Biznetic.tortoizthemes.com
 * Author: TortoizThemes
 * Author URI: https://tortoizthemes.com
 * Description: Biznetic is a modern, responsive, and customizable Bootstrap-based theme for your business website.
 * Version: 3.0.0
 */
/**
 * GLOSSARY
 * 1. Sticky Header (Headroom.js)
 *    - Handles the sticky navigation bar with smooth show/hide on scroll
 *    - Includes scroll direction detection for better UX
 * 
 * 2. Navigation
 *    - Active state management for navigation items
 *    - Supports both main navigation and dropdown menus
 *    - Automatically highlights current page in navigation
 *
 * 3. Swiper Sliders
 *    - Projects Swiper: Responsive carousel for project showcases
 *    - Timeline Swiper: Horizontal timeline with responsive breakpoints
 *    - Features: Loop, navigation, pagination, autoplay, and responsive settings
 *
 * 4. LightGallery (Video Popup)
 *    - Lightweight video lightbox with YouTube/Vimeo support
 *    - Mobile-friendly touch controls
 *    - Autoplay and loop options
 *
 * 5. Progress Bars
 *    - Animated progress bars that trigger on scroll
 *    - Uses data attributes for configuration
 *    - Single-run animation (animates only once when scrolled into view)
 *
 * 6. Portfolio Filter & Load More
 *    - Category-based filtering for portfolio items
 *    - AJAX-like "Load More" functionality
 *    - Smooth animations and loading states
 *
 * 7. Utility Functions
 *    - Responsive breakpoint handling
 *    - Scroll event optimizations
 *    - Error handling and fallbacks
 */
// Global variables for sticky header
let headroomInstance = null;
let stickyHeaderInitialized = false;

/**
 * Sticky Header
 * Enables sticky behavior on navbar on page scroll using Headroom.js
 */
function initStickyHeader() {
    const navbar = document.querySelector(".navbar");
    if (!navbar) return;
    
    // Safely destroy existing instance if it exists
    if (headroomInstance && typeof headroomInstance.destroy === 'function') {
        try {
            headroomInstance.destroy();
        } catch (e) {
            console.warn('Error cleaning up previous Headroom instance:', e);
        }
        headroomInstance = null;
    }
    
    // Initialize Headroom
    headroomInstance = new Headroom(navbar, {
        offset: 200, // Pixels the element should move before being unpinned
        tolerance: 5, // Scroll tolerance in px before state changes
        classes: {
            initial: 'headroom',
            pinned: 'headroom--pinned',
            unpinned: 'headroom--unpinned',
            top: 'headroom--top',
            notTop: 'headroom--not-top',
            bottom: 'headroom--bottom',
            notBottom: 'headroom--not-bottom',
            frozen: 'headroom--frozen'
        },
        onPin: function() {
            document.body.classList.add('sticky-header-active');
            navbar.classList.add('shadow-sm', 'animate__animated', 'animate__fadeInDown');
        },
        onUnpin: function() {
            document.body.classList.remove('sticky-header-active');
            navbar.classList.remove('shadow-sm', 'animate__animated', 'animate__fadeInDown');
        },
        onTop: function() {
            navbar.classList.remove('navbar-scroll-down');
            navbar.classList.add('navbar-scroll-up');
        },
        onNotTop: function() {
            // Additional logic when not at top
        },
        onBottom: function() {
            // When scrolled to bottom
        },
        onNotBottom: function() {
            // When not at bottom
        }
    });
    
    // Initialize Headroom
    headroomInstance.init();
    
    // Add scroll event for additional effects
    let lastScrollTop = 0;
    const delta = 5;
    const navbarHeight = navbar.offsetHeight;
    
    window.addEventListener('scroll', () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // Only run if scrolled more than delta
        if (Math.abs(lastScrollTop - scrollTop) <= delta) return;
        
        // Scrolling down
        if (scrollTop > lastScrollTop && scrollTop > navbarHeight) {
            navbar.classList.remove('navbar-scroll-up');
            navbar.classList.add('navbar-scroll-down');
        } else {
            // Scroll up
            if (scrollTop + window.innerHeight < document.documentElement.scrollHeight) {
                navbar.classList.remove('navbar-scroll-down');
                navbar.classList.add('navbar-scroll-up');
            }
        }
        
        lastScrollTop = scrollTop <= 0 ? 0 : scrollTop; // For Mobile or negative scrolling
    });
    
    // Handle resize events
    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            if (headroomInstance && typeof headroomInstance.destroy === 'function') {
                try {
                    headroomInstance.destroy();
                } catch (e) {
                    console.warn('Error destroying Headroom instance:', e);
                } finally {
                    headroomInstance = null;
                    stickyHeaderInitialized = false;
                    initStickyHeader();
                }
            } else {
                stickyHeaderInitialized = false;
                initStickyHeader();
            }
        }, 250);
    });
    
    // Mark as initialized
    stickyHeaderInitialized = true;
}

// Check if Headroom is loaded
function isHeadroomLoaded() {
    return typeof Headroom !== 'undefined';
}

/**
 * Set active state for navigation items based on current page
 */
function setActiveNavItem() {
    // Get current page path
    const currentPath = window.location.pathname.split('/').pop() || 'index.html';
    
    // Find all nav links
    const navLinks = document.querySelectorAll('.navbar-nav .nav-link');
    const dropdownItems = document.querySelectorAll('.dropdown-menu .dropdown-item');
    
    // Function to set active class
    function setActiveLink(links) {
        links.forEach(link => {
            const linkPath = link.getAttribute('href')?.split('/').pop();
            if (!linkPath) return;
            
            const parentLi = link.closest('li');
            const parentDropdown = link.closest('.dropdown');
            
            // Check if current path matches the link
            if (currentPath === linkPath || 
                (currentPath === '' && linkPath === 'index.html')) {
                
                // Remove active class from all links and their parents
                navLinks.forEach(navLink => {
                    navLink.classList.remove('active');
                    navLink.closest('li')?.classList.remove('active');
                    navLink.closest('.dropdown')?.classList.remove('active');
                });
                
                // Add active class to current link and its parents
                link.classList.add('active');
                parentLi?.classList.add('active');
                parentDropdown?.classList.add('active');
                
                // If it's a dropdown item, also activate the parent dropdown toggle
                if (link.classList.contains('dropdown-item')) {
                    const dropdownToggle = parentDropdown?.querySelector('.dropdown-toggle');
                    dropdownToggle?.classList.add('active');
                }
            }
        });
    }
    
    // Set active state for both main nav links and dropdown items
    setActiveLink([...navLinks, ...dropdownItems]);
}

// Initialize sticky header when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
    // Set active navigation item based on current page
    setActiveNavItem();
    
    // Function to safely initialize sticky header
    const initStickyHeaderSafe = () => {
        try {
            initStickyHeader();
        } catch (error) {
            console.error('Error initializing sticky header:', error);
            // Reset state to allow retries
            stickyHeaderInitialized = false;
            headroomInstance = null;
        }
    };
    
    // Initialize sticky header with Headroom.js
    if (typeof Headroom === 'undefined') {
        // If Headroom is not loaded yet, try again after a delay
        let retryCount = 0;
        const maxRetries = 5;
        const checkHeadroom = setInterval(function() {
            if (typeof Headroom !== 'undefined') {
                clearInterval(checkHeadroom);
                initStickyHeaderSafe();
            } else if (++retryCount >= maxRetries) {
                clearInterval(checkHeadroom);
                console.warn('Headroom.js not loaded after multiple attempts. Sticky header disabled.');
            }
        }, 200);
    } else {
        initStickyHeaderSafe();
    }
});

// Timeline swiper js
document.addEventListener('DOMContentLoaded', function() {
    // Initialize Timeline Swiper if element exists
    const timelineSwiper = document.querySelector('.timeline-swiper');
    if (timelineSwiper && typeof Swiper !== 'undefined') {
        new Swiper(timelineSwiper, {
            direction: 'horizontal',
            loop: false,
            slidesPerView: 4,
            spaceBetween: 30,
            centeredSlides: false,
            slideToClickedSlide: true,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                320: {
                    slidesPerView: 1,
                    spaceBetween: 20
                },
                576: {
                    slidesPerView: 2,
                    spaceBetween: 20
                },
                768: {
                    slidesPerView: 3,
                    spaceBetween: 30
                },
                1200: {
                    slidesPerView: 4,
                    spaceBetween: 30
                }
            }
        });
    }
    
    const projectsSwiper = document.querySelector('.projects-swiper');
    if (!projectsSwiper) return; // Exit if projects swiper not found
    
    // Check if Swiper is loaded
    if (typeof Swiper === 'undefined') return; // Exit if Swiper not loaded

    try {
        const swiper = new Swiper(projectsSwiper, {
            // Enable debugger
            init: true,
            
            // Optional parameters
            loop: true,
            centeredSlides: true,
            grabCursor: true,
            observer: true,
            observeParents: true,
            watchSlidesProgress: true,
            watchSlidesVisibility: true,
            
            // Navigation arrows
            navigation: {
                nextEl: '.swiper-button-next-projects',
                prevEl: '.swiper-button-prev-projects',
                disabledClass: 'swiper-button-disabled',
                hiddenClass: 'swiper-button-hidden'
            },
            
            // Pagination - disabled on all screen sizes
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 3,
                //enabled: window.innerWidth >= 768 // Enable only on screens ≥ 768px
            },
            
            // Autoplay
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
                waitForTransition: true
            },
            
            // Responsive breakpoints
            breakpoints: {
                320: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                    pagination: {
                        enabled: false // Disable pagination on mobile
                    }
                },
                576: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                    pagination: {
                        enabled: false // Disable pagination on small tablets
                    }
                },
                768: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                    pagination: {
                        enabled: false // Enable pagination on tablets and up
                    }
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                    pagination: {
                        enabled: true // Keep pagination enabled on desktops
                    }
                }
            },
            
            // Events
            on: {
                init: function() {
                    console.log('Swiper initialized successfully');
                    document.body.classList.add('swiper-loaded');
                },
                slideChange: function() {
                    console.log('Slide changed to: ' + this.activeIndex);
                },
                resize: function() {
                    console.log('Swiper: resized');
                }
            }
        });
        
        // Make swiper instance available globally for debugging
        window.mySwiper = swiper;
        
    } catch (error) {
        console.error('Error initializing Swiper:', error);
    }
});

// Initialize GLightbox for video popup
document.addEventListener('DOMContentLoaded', function() {
    // Only initialize if there are video popup elements and GLightbox is loaded
    if (document.querySelector('.video-popup') && typeof GLightbox !== 'undefined') {
        const lightbox = GLightbox({
            selector: '.video-popup',
            touchNavigation: true,
            loop: true,
            autoplayVideos: true,
            plyr: {
                config: {
                    ratio: '16:9',
                    youtube: {
                        noCookie: true,
                        rel: 0,
                        showinfo: 0,
                        iv_load_policy: 3
                    },
                    vimeo: {
                        byline: false,
                        portrait: false,
                        title: false,
                        transparent: false
                    }
                }
            }
        });
    }
});

// Initialize progress bars animation
// Animate progress bars when they come into view
document.addEventListener('DOMContentLoaded', function() {
    const animateProgressBars = () => {
      document.querySelectorAll('[data-bs-toggle="progress-bar"]').forEach(bar => {
        const rect = bar.getBoundingClientRect();
        const isInView = (
          rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.bottom >= 0 &&
          !bar.classList.contains('animated')
        );
        
        if (isInView) {
          const targetWidth = bar.getAttribute('data-bs-target') + '%';
          bar.style.width = targetWidth;
          bar.classList.add('animated');
        }
      });
    };
  
    // Run once on load
    animateProgressBars();
    
    // Run on scroll
    window.addEventListener('scroll', animateProgressBars);
});

//portfolio section
document.addEventListener('DOMContentLoaded', function() {
    // Only initialize portfolio if the container exists
    const portfolioContainer = document.querySelector('.portfolio-grid');
    if (!portfolioContainer) return;
    
    const filterButtons = document.querySelectorAll('.portfolio-filter button');
    const loadMoreBtn = document.getElementById('loadMore');
    const btnText = document.querySelector('.btn-text');
    const spinner = document.querySelector('.spinner-border');
    const itemTemplate = document.getElementById('portfolioItemTemplate');
    
    // Get all portfolio items
    const allItems = Array.from(document.querySelectorAll('.portfolio-item'));
    if (allItems.length === 0) return;
    
    // Initial items to show and items per load
    let itemsToShow = 6;
    let currentItems = 0;
    
    // Hide all items initially
    function initPortfolio() {
      allItems.forEach((item, index) => {
        if (index < itemsToShow) {
          item.style.display = 'block';
          item.classList.add('animate__animated', 'animate__fadeIn');
          currentItems++;
        } else {
          item.style.display = 'none';
        }
      });
      
      updateLoadMoreButton();
    }
    
    // Update Load More button state
    function updateLoadMoreButton() {
      if (currentItems >= allItems.length) {
        loadMoreBtn.style.display = 'none';
      } else {
        loadMoreBtn.style.display = 'inline-flex';
      }
    }
    
    // Load more items
    function loadMoreItems() {
      loadMoreBtn.disabled = true;
      btnText.textContent = 'Loading...';
      spinner.classList.remove('d-none');
      
      // Simulate loading delay
      setTimeout(() => {
        const itemsToAdd = [];
        const nextItems = allItems.slice(currentItems, currentItems + 3);
        
        nextItems.forEach(item => {
          item.style.display = 'block';
          item.classList.add('animate__animated', 'animate__fadeIn');
          item.style.animationDelay = `${currentItems * 0.1}s`;
          currentItems++;
        });
        
        updateLoadMoreButton();
        
        // Reset button state
        loadMoreBtn.disabled = false;
        btnText.textContent = 'Load More';
        spinner.classList.add('d-none');
        
        // Scroll to newly loaded items
        nextItems[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 800);
    }
    
    // Filter portfolio items
    function filterItems(filterValue) {
      currentItems = 0;
      allItems = Array.from(document.querySelectorAll('.portfolio-item'));
      const filteredItems = filterValue === '*' 
        ? allItems 
        : allItems.filter(item => item.classList.contains(filterValue.replace('.', '')));
      
      // Hide all items first
      allItems.forEach(item => {
        item.style.display = 'none';
        item.classList.remove('animate__animated', 'animate__fadeIn');
      });
      
      // Show filtered items
      filteredItems.forEach((item, index) => {
        if (index < itemsToShow) {
          item.style.display = 'block';
          item.classList.add('animate__animated', 'animate__fadeIn');
          currentItems++;
        }
      });
      
      allItems = filteredItems;
      updateLoadMoreButton();
    }
    
    // Event Listeners
    loadMoreBtn.addEventListener('click', loadMoreItems);
    
    filterButtons.forEach(button => {
      button.addEventListener('click', function() {
        // Remove active class from all buttons
        filterButtons.forEach(btn => btn.classList.remove('active'));
        // Add active class to clicked button
        this.classList.add('active');
        
        const filterValue = this.getAttribute('data-filter');
        filterItems(filterValue);
      });
    });
    
    // Initialize
    initPortfolio();
  });

